import { useState, useRef, useEffect } from 'react';
import { APIProvider, Map, AdvancedMarker, useMapsLibrary } from '@vis.gl/react-google-maps';
import { object, string, func, bool, arrayOf } from 'prop-types';
import TextType from './TextType';
import Div from '@ui/layout/Div';
import { useTranslation } from 'react-i18next';
import Alert from '../elements/Alert';

const AddressAutocomplete = ({
  centerCoordinates,
  country,
  name,
  onChange,
  placeholder,
  label,
  help,
  error,
  isRequired,
  cssClass,
  types = ['address'],
  targetMairie = null,
  targetZipCode = null,
  zipCodeField
}) => {
  const [t] = useTranslation('sterilization')
  const [address, setAddress] = useState('');
  const [addressName, setAddressName] = useState('');

  const [location, setLocation] = useState(null);
  const [isValid, setIsValid] = useState(true);
  const [zipCode, setZipCode] = useState(null);
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const placesLibrary = useMapsLibrary('places');

  const defaultBounds = {
    north: centerCoordinates.lat + 0.0,
    south: centerCoordinates.lat - 0.0,
    east: centerCoordinates.lng + 0.0,
    west: centerCoordinates.lng - 0.0,
  };

  const isValidLocation = (place) => {
    let mairie = null
    setIsValid(true)
    place.address_components.forEach(component => {
      if (component.types.includes('locality') && component.types.includes('political')) {
        mairie = component.long_name.toLowerCase();
      }
      if (component.types.includes('postal_code')) {
        setZipCode(component.long_name);
      }
    })

    if (targetMairie && targetMairie.toLowerCase() !== mairie) {
      setIsValid(false)
    }
    if (targetZipCode && targetZipCode.split(',').indexOf(zipCode) === -1) {
      setIsValid(false)
    }
  }

  useEffect(() => {
    if (!placesLibrary || !inputRef.current) return;

    autocompleteRef.current = new placesLibrary.Autocomplete(inputRef.current, {
      bounds: defaultBounds,
      componentRestrictions: { country },
      fields: ['formatted_address', "name", 'geometry', 'address_components'],
      types,
    });

    const handlePlaceSelect = async () => {
      const place = autocompleteRef.current.getPlace();
      await isValidLocation(place)
      if (place?.geometry?.location && isValid) {
        setAddress(place.formatted_address);
        setAddressName(place.name);
        setLocation({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        });
      }
    };

    autocompleteRef.current.addListener('place_changed', handlePlaceSelect);

    return () => {
      if (autocompleteRef.current) {
        // eslint-disable-next-line no-undef
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [placesLibrary]);

  useEffect(() => {
    let object = { [name]: addressName }
    if (isValid && zipCodeField && zipCode) {
      object[zipCodeField] = zipCode
    }
    onChange(null, object)
  }, [isValid, zipCode, addressName])

  return (
    <Div cssClass={`autocomplete-field ${cssClass}`}>
      <TextType
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        placeholder={placeholder || ''}
        label={label}
        help={help}
        error={error}
        isRequired={isRequired}
        otherProps={{ ref: inputRef }}
      />

      {location && isValid && <>
        <Div style={{ height: '400px', width: '100%' }}>
          <Map
            defaultCenter={{ lat: 40.7128, lng: -74.0060 }}
            defaultZoom={12}
            center={location || { lat: 40.7128, lng: -74.0060 }}
            zoom={location ? 15 : 12}
            mapId={`sterilization-address-call-preview-location`}
          >
            {location && <AdvancedMarker position={location} />}
          </Map>
        </Div>
      </>}
      {!isValid && <Alert type='is-danger' message={t('messages.wrong_location')} />}
    </Div>
  );
};

function PlaceAutocomplete ({
  name,
  centerCoordinates = { lat: 46.71109, lng: 1.7191036 },
  country = 'fr',
  onChange,
  placeholder,
  label,
  help,
  error,
  isRequired,
  cssClass,
  types = ['address'],
  targetMairie = null,
  targetZipCode = null,
  zipCodeField = 'sterilizationZipCode'
}) {
  return (
    <APIProvider apiKey={import.meta.env.VITE_MAPS_KEY}>
      <AddressAutocomplete centerCoordinates={centerCoordinates} country={country} name={name} onChange={onChange} placeholder={placeholder} label={label} help={help} error={error} isRequired={isRequired} cssClass={cssClass} targetMairie={targetMairie} targetZipCode={targetZipCode} types={types} zipCodeField={zipCodeField} />
    </APIProvider>
  );
}

AddressAutocomplete.propTypes = {
  name: string,
  centerCoordinates: object,
  country: string,
  onChange: func,
  placeholder: string,
  help: string,
  error: string,
  isRequired: bool,
  label: string,
  cssClass: string,
  targetMairie: string,
  targetZipCode: string,
  types: arrayOf(string),
  zipCodeField: string
}

PlaceAutocomplete.propTypes = {
  name: string,
  centerCoordinates: object,
  country: string,
  onChange: func,
  placeholder: string,
  help: string,
  error: string,
  isRequired: bool,
  label: string,
  cssClass: string,
  types: arrayOf(string),
  targetMairie: string,
  targetZipCode: string,
  zipCodeField: string
}

export default PlaceAutocomplete;